import { z } from 'zod';
import { deliveryPlanningChangeSchema } from './delivery-planning.js';
import { EXPIRY_LEVELS, SHELF_LIFE_WARNING_DAYS, STOCK_ADJUSTMENT_REASON_MIN_LENGTH, } from '../constants/products.js';
import { commaSeparated, countryCodeSchema, idSchema, isoDateSchema, isoDateTimeSchema, moneySchema, nonNegativeQuantitySchema, positiveQuantitySchema, quantitySchema, } from './common.js';
import { customFieldValuesSchema } from './custom-field.js';
import { batchStatusSchema, pickingStrategySchema, stockMovementTypeSchema, stockReferenceTypeSchema, } from './enums.js';
import { paginationMetaSchema, paginationQuerySchema, sortOrderSchema } from './pagination.js';
const optionalText = (max) => z.string().trim().max(max).nullish();
export const expiryLevelSchema = z.enum(EXPIRY_LEVELS);
// ---------------------------------------------------------------------------
// Chargen
// ---------------------------------------------------------------------------
export const productBatchSchema = z.object({
    id: idSchema,
    productId: idSchema,
    productArticleNumber: z.string(),
    productName: z.string(),
    batchNumber: z.string(),
    supplierBatchNumber: z.string().nullable(),
    status: batchStatusSchema,
    receivedAt: isoDateSchema,
    productionDate: isoDateSchema.nullable(),
    bestBeforeDate: isoDateSchema.nullable(),
    expiryDate: isoDateSchema.nullable(),
    initialQuantity: z.string(),
    remainingQuantity: z.string(),
    reservedQuantity: z.string(),
    availableQuantity: z.string(),
    unitCost: z.string().nullable(),
    warehouseCode: z.string().nullable(),
    storageLocation: z.string().nullable(),
    countryOfOrigin: z.string().nullable(),
    certificateRef: z.string().nullable(),
    notes: z.string().nullable(),
    customFields: customFieldValuesSchema,
    /** Ampelstufe aus Restlaufzeit; wird serverseitig gegen den Tagesstichtag berechnet. */
    expiryLevel: expiryLevelSchema,
    remainingShelfLifeDays: z.number().int().nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export function validateBatchDateSequence(value, ctx) {
    if (typeof value.productionDate === 'string' &&
        typeof value.bestBeforeDate === 'string' &&
        value.bestBeforeDate < value.productionDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['bestBeforeDate'],
            message: 'Das MHD darf nicht vor dem Produktionsdatum liegen.',
        });
    }
    if (typeof value.bestBeforeDate === 'string' &&
        typeof value.expiryDate === 'string' &&
        value.expiryDate < value.bestBeforeDate) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['expiryDate'],
            message: 'Das Verfallsdatum darf nicht vor dem MHD liegen.',
        });
    }
}
const productBatchFieldsSchema = z.object({
    batchNumber: z.string().trim().min(1).max(60),
    supplierBatchNumber: optionalText(60),
    quantity: positiveQuantitySchema,
    receivedAt: isoDateSchema.optional(),
    productionDate: isoDateSchema.nullish(),
    bestBeforeDate: isoDateSchema.nullish(),
    expiryDate: isoDateSchema.nullish(),
    unitCost: moneySchema.nullish(),
    warehouseCode: optionalText(20),
    storageLocation: optionalText(40),
    countryOfOrigin: countryCodeSchema.nullish(),
    certificateRef: optionalText(80),
    notes: optionalText(2000),
    status: batchStatusSchema.default('FREE'),
    referenceType: stockReferenceTypeSchema.default('MANUAL'),
    referenceId: idSchema.nullish(),
    customFields: customFieldValuesSchema.default({}),
});
export const productBatchCreateSchema = productBatchFieldsSchema.superRefine(validateBatchDateSequence);
/** Batch metadata only; stock is posted by the enclosing adjustment. */
export const adjustmentBatchSchema = productBatchFieldsSchema
    .omit({
    quantity: true,
    referenceType: true,
    referenceId: true,
    warehouseCode: true,
    unitCost: true,
})
    .superRefine(validateBatchDateSequence);
/** Nur bestandsneutrale Felder sind änderbar; Mengen laufen ausschließlich über Buchungen. */
export const productBatchUpdateSchema = z
    .object({
    status: batchStatusSchema.optional(),
    supplierBatchNumber: optionalText(60),
    productionDate: isoDateSchema.nullish(),
    bestBeforeDate: isoDateSchema.nullish(),
    expiryDate: isoDateSchema.nullish(),
    warehouseCode: optionalText(20),
    storageLocation: optionalText(40),
    certificateRef: optionalText(80),
    notes: optionalText(2000),
    reason: z.string().trim().max(500).nullish(),
    customFields: customFieldValuesSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.status === 'BLOCKED' && (value.reason ?? '').trim().length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reason'],
            message: 'Für eine Chargensperre ist eine Begründung erforderlich.',
        });
    }
});
export const productBatchListQuerySchema = z.object({
    status: commaSeparated(batchStatusSchema),
    expiryLevel: commaSeparated(expiryLevelSchema),
    includeDepleted: z.preprocess((value) => value === 'true', z.boolean()).default(false),
    referenceDate: isoDateSchema.optional(),
});
export const productBatchListResponseSchema = z.object({
    data: z.array(productBatchSchema),
    meta: z.object({
        total: z.number().int(),
        totalRemaining: z.string(),
        byLevel: z.object({
            EXPIRED: z.number().int(),
            CRITICAL: z.number().int(),
            WARNING: z.number().int(),
            OK: z.number().int(),
        }),
    }),
});
// ---------------------------------------------------------------------------
// Bestandsbuchungen
// ---------------------------------------------------------------------------
export const stockMovementSchema = z.object({
    id: idSchema,
    productId: idSchema,
    batchId: idSchema.nullable(),
    batchNumber: z.string().nullable(),
    type: stockMovementTypeSchema,
    referenceType: stockReferenceTypeSchema,
    referenceId: z.string().nullable(),
    quantityBase: z.string(),
    balanceAfter: z.string(),
    unitCost: z.string().nullable(),
    warehouseCode: z.string().nullable(),
    reason: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
    createdAt: isoDateTimeSchema,
    createdById: idSchema,
    createdByName: z.string(),
});
export const stockAdjustmentSchema = z
    .object({
    idempotencyKey: z.string().uuid(),
    newBatch: adjustmentBatchSchema.optional(),
    /** Signierte Korrekturmenge; alternativ `countedQuantity` für eine Inventurzählung. */
    quantity: quantitySchema.optional(),
    countedQuantity: nonNegativeQuantitySchema.optional(),
    batchId: idSchema.nullish(),
    type: stockMovementTypeSchema.default('ADJUSTMENT'),
    referenceType: stockReferenceTypeSchema.default('MANUAL'),
    referenceId: idSchema.nullish(),
    warehouseCode: optionalText(20),
    unitCost: moneySchema.nullish(),
    reason: z.string().trim().min(STOCK_ADJUSTMENT_REASON_MIN_LENGTH).max(500),
    occurredAt: isoDateTimeSchema.optional(),
})
    .superRefine((value, ctx) => {
    if (value.newBatch !== undefined && value.batchId !== null && value.batchId !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['batchId'],
            message: 'stockAdjustment.chooseOneBatch',
        });
    }
    if (value.newBatch !== undefined && Number(value.quantity ?? value.countedQuantity) <= 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quantity'],
            message: 'stockAdjustment.newBatchPositive',
        });
    }
    const hasQuantity = value.quantity !== undefined;
    const hasCount = value.countedQuantity !== undefined;
    if (hasQuantity === hasCount) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quantity'],
            message: 'Entweder eine Korrekturmenge oder eine gezählte Menge angeben.',
        });
        return;
    }
    if (hasQuantity && Number(value.quantity) === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['quantity'],
            message: 'Die Korrekturmenge darf nicht null sein.',
        });
    }
});
export const stockAdjustmentResultSchema = z.object({
    movement: stockMovementSchema,
    stockQuantity: z.string(),
    availableQuantity: z.string(),
    batchRemainingQuantity: z.string().nullable(),
});
export const stockMovementSortFieldSchema = z.enum(['occurredAt', 'createdAt', 'quantityBase']);
export const stockMovementListQuerySchema = paginationQuerySchema
    .omit({ sort: true, q: true })
    .extend({
    sort: z
        .string()
        .regex(/^[a-zA-Z]+:(asc|desc)$/)
        .optional(),
    type: commaSeparated(stockMovementTypeSchema),
    referenceType: commaSeparated(stockReferenceTypeSchema),
    batchId: idSchema.optional(),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
})
    .transform((value) => {
    const [field, order] = (value.sort ?? 'occurredAt:desc').split(':');
    const parsedField = stockMovementSortFieldSchema.safeParse(field);
    const parsedOrder = sortOrderSchema.safeParse(order);
    return {
        ...value,
        sortField: parsedField.success ? parsedField.data : 'occurredAt',
        sortOrder: parsedOrder.success ? parsedOrder.data : 'desc',
    };
});
export const stockMovementListResponseSchema = z.object({
    data: z.array(stockMovementSchema),
    meta: paginationMetaSchema,
});
// ---------------------------------------------------------------------------
// Chargenvorschlag und MHD-Monitor
// ---------------------------------------------------------------------------
export const nextBatchQuerySchema = z.object({
    quantity: positiveQuantitySchema.default('1'),
    strategy: pickingStrategySchema.optional(),
    warehouseCode: z.string().trim().min(1).max(20).optional(),
    referenceDate: isoDateSchema.optional(),
});
export const batchAllocationSchema = z.object({
    batchId: idSchema,
    batchNumber: z.string(),
    quantity: z.string(),
    bestBeforeDate: isoDateSchema.nullable(),
    expiryLevel: expiryLevelSchema,
    storageLocation: z.string().nullable(),
});
export const batchSuitabilityContextSchema = z.object({
    referenceDate: isoDateSchema.nullable(),
    minimumDays: z.number().int().min(0),
    requirementSource: z.enum(['DOCUMENT', 'ADDRESS', 'CUSTOMER', 'PRODUCT', 'NONE']),
    warehouseCode: z.string().nullable(),
});
export const batchCandidateSchema = z.object({
    selectableQuantity: z.string(),
    batchId: idSchema,
    batchNumber: z.string(),
    availableQuantity: z.string(),
    bestBeforeDate: isoDateSchema.nullable(),
    expiryDate: isoDateSchema.nullable(),
    relevantDate: isoDateSchema.nullable(),
    dateKind: z.enum(['BEST_BEFORE', 'EXPIRY', 'NONE']),
    remainingDays: z.number().int().nullable(),
    status: batchStatusSchema,
    warehouseCode: z.string().nullable(),
    reason: z.enum([
        'SUITABLE',
        'BLOCKED',
        'NO_STOCK',
        'WAREHOUSE',
        'MISSING_DATE',
        'MISSING_EXPIRY',
        'EXPIRED',
        'TOO_SHORT',
    ]),
});
export const deliveryBatchProposalSchema = z.object({
    productId: idSchema,
    customerId: idSchema,
    documentId: idSchema.optional(),
    shippingAddressId: idSchema.nullable(),
    deliveryDate: isoDateSchema.nullable(),
    deliveryPlanningChange: deliveryPlanningChangeSchema.optional(),
    quantity: positiveQuantitySchema,
    packagingFactor: positiveQuantitySchema.default('1'),
    assigned: z
        .array(z.object({ batchId: idSchema, quantity: nonNegativeQuantitySchema }))
        .max(1000)
        .default([]),
});
export const nextBatchResponseSchema = z.object({
    productId: idSchema,
    strategy: pickingStrategySchema,
    requestedQuantity: z.string(),
    allocatedQuantity: z.string(),
    shortfallQuantity: z.string(),
    allocations: z.array(batchAllocationSchema),
    candidates: z.array(batchCandidateSchema),
    context: batchSuitabilityContextSchema,
    unitCode: z.string(),
});
export const expiringBatchesQuerySchema = z.object({
    withinDays: z.coerce.number().int().min(1).max(3650).default(SHELF_LIFE_WARNING_DAYS),
    limit: z.coerce.number().int().min(1).max(100).default(20),
    includeExpired: z.preprocess((value) => value !== 'false', z.boolean()).default(true),
    referenceDate: isoDateSchema.optional(),
});
export const expiringBatchSchema = productBatchSchema.pick({
    id: true,
    productId: true,
    productArticleNumber: true,
    productName: true,
    batchNumber: true,
    status: true,
    bestBeforeDate: true,
    remainingQuantity: true,
    storageLocation: true,
    expiryLevel: true,
    remainingShelfLifeDays: true,
});
export const expiringBatchesResponseSchema = z.object({
    data: z.array(expiringBatchSchema),
    meta: z.object({
        total: z.number().int(),
        expired: z.number().int(),
        critical: z.number().int(),
        warning: z.number().int(),
    }),
});
//# sourceMappingURL=inventory.js.map